import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardTitle } from '@patternfly/react-core';
import { Table, Tbody, Tr, Th, Td } from '@patternfly/react-table';
import FormattedDateTime from 'components/FormattedDateTime';
import type { LabDriverComponentProps } from '../types';
import './styles.scss';

type DisplayTableRow = {
  key: string;
  title: string;
  value: string;
};

type InstallEvent = {
  '@timestamp': string;
  play: string;
};

type TowerContext = {
  guid?: string;
  workstation_password?: string;
  state?: string;
  stat?: {
    display_table?: DisplayTableRow[];
    probe_status?: { status?: string };
    events?: InstallEvent[];
  };
};

const TowerLabComponent: React.FC<LabDriverComponentProps> = ({
  labComponent,
}) => {
  const { t } = useTranslation();
  const context = labComponent.context as TowerContext | undefined;

  const renderLabPanel = () => {
    if (!context?.guid) {
      return null;
    }

    const url = `workstation.${context.guid}.do417.prod.nextcle.com`;
    const displayTable = context.stat?.display_table;

    if (displayTable?.length) {
      return (
        <Card className="tower-lab-component__card">
          <CardTitle>{t('Provisioned Lab Details')}</CardTitle>
          <CardBody>
            <Table variant="compact" borders={false}>
              <Tbody>
                {displayTable.map((row) => (
                  <Tr key={row.key}>
                    <Th>{row.title}</Th>
                    <Td>{row.value}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      );
    }

    const classroomStatus = context.stat?.probe_status?.status || 'inactive';

    return (
      <Card className="tower-lab-component__card">
        <CardTitle>{t('Provisioned Lab Details')}</CardTitle>
        <CardBody>
          <Table variant="compact" borders={false}>
            <Tbody>
              <Tr>
                <Th>GUID</Th>
                <Td>{context.guid}</Td>
              </Tr>
              <Tr>
                <Th>{t('Workstation Hostname')}</Th>
                <Td>{url}</Td>
              </Tr>
              <Tr>
                <Th>{t('Workstation Password')}</Th>
                <Td>{context.workstation_password}</Td>
              </Tr>
              <Tr>
                <Th>{t('State')}</Th>
                <Td>{context.state}</Td>
              </Tr>
              <Tr>
                <Th>{t('Classroom Status')}</Th>
                <Td>{classroomStatus}</Td>
              </Tr>
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    );
  };

  const renderInstallationProgress = () => {
    if (!context?.guid) {
      return null;
    }

    const isInstalling =
      context.state === 'installing' || context.state === 'unavailable';
    if (!isInstalling) {
      return null;
    }

    const installEvents = context.stat?.events;
    if (!installEvents) {
      return null;
    }

    return (
      <Card className="tower-lab-component__card">
        <CardTitle>{t('Installation Progress')}</CardTitle>
        <CardBody>
          <Table variant="compact" borders={false}>
            <Tbody>
              {installEvents.map((event) => (
                <Tr key={event['@timestamp']}>
                  <Td>
                    <FormattedDateTime>{event['@timestamp']}</FormattedDateTime>
                  </Td>
                  <Td>{event.play}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    );
  };

  return (
    <div>
      {renderLabPanel()}
      {renderInstallationProgress()}
    </div>
  );
};

export default observer(TowerLabComponent);
